import { useState, useEffect } from 'react';
import { schedulesAPI, termsAPI, classesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Toaster, toast } from 'sonner';
import { Calendar, Clock, MapPin, User } from 'lucide-react';
import Navbar from '../components/Navbar';
import { formatTime, DAYS_OF_WEEK, getCategoryInfo } from '../lib/utils';
import { FreeTrialBanner } from '../components/FreeTrialCTA';
import SEO from '../components/common/SEO';

const Schedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [classes, setClasses] = useState([]);
  const [activeTerm, setActiveTerm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, classesRes, termRes] = await Promise.all([
        schedulesAPI.getAll(),
        classesAPI.getAll(),
        termsAPI.getActive(),
      ]);
      setSchedules(schedulesRes.data);
      setClasses(classesRes.data);
      setActiveTerm(termRes.data);
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const getClassInfo = (classId) => {
    return classes.find(c => c.id === classId);
  };

  const getCategoryColor = (category) => {
    const colors = {
      ballet: 'bg-pink-500/20 text-pink-300 border-pink-500/30',
      drama: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      singing: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      dance: 'bg-green-500/20 text-green-300 border-green-500/30',
      'street-dance': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      'hip-hop': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      tap: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      musical_theatre: 'bg-red-500/20 text-red-300 border-red-500/30',
      default: 'bg-[#FF007F]/20 text-[#FF007F] border-[#FF007F]/30'
    };
    return colors[category] || colors.default;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SEO page="schedule" />
      <Navbar />
      <Toaster position="top-right" richColors />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="bg-gradient-to-r from-[#FF007F] to-[#7000FF] text-white border-0 mb-4">
            <Calendar className="w-3 h-3 mr-1" />
            {activeTerm?.name || 'Current Term'}
          </Badge>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2">
            WEEKLY <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF007F] to-[#7000FF]">SCHEDULE</span>
          </h1>
          <p className="text-white/60">
            View all classes throughout the week
          </p>
        </div>

        {/* Free Trial CTA */}
        <div className="mb-8 rounded-xl overflow-hidden">
          <FreeTrialBanner variant="dark" />
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-[#111] rounded-xl animate-pulse" />
            ))}
          </div>
        ) : schedules.length === 0 ? (
          <Card className="bg-[#111] border-[#333]">
            <CardContent className="py-16 text-center">
              <Calendar className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-white mb-2">
                No schedules available
              </h3>
              <p className="text-white/60">
                Check back later for upcoming classes.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {DAYS_OF_WEEK.map((day, dayIndex) => {
              const daySchedules = schedules
                .filter(s => s.day_of_week === dayIndex)
                .sort((a, b) => a.start_time.localeCompare(b.start_time));

              if (daySchedules.length === 0) return null;

              const isToday = new Date().getDay() === (dayIndex === 6 ? 0 : dayIndex + 1);

              return (
                <Card key={day} className={`bg-[#111] border-[#333] overflow-hidden ${isToday ? 'ring-2 ring-[#FF007F]' : ''}`}>
                  <CardHeader className="bg-gradient-to-r from-[#FF007F]/10 to-[#7000FF]/10 border-b border-[#333]">
                    <CardTitle className="flex items-center gap-3 text-white">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#FF007F] to-[#7000FF] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">{day.slice(0, 2)}</span>
                      </div>
                      {day}
                      {isToday && <Badge className="bg-[#FF007F] text-white ml-2">Today</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {daySchedules.map(schedule => {
                        const classInfo = getClassInfo(schedule.class_id);
                        const categoryInfo = classInfo ? getCategoryInfo(classInfo.category) : null;

                        return (
                          <div
                            key={schedule.id}
                            className="p-4 bg-[#1a1a1a] rounded-xl border border-[#333] hover:border-[#FF007F]/50 hover:shadow-lg hover:-translate-y-1 transition-all cursor-pointer"
                            onClick={() => window.location.href = `/classes/${schedule.class_id}`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <Badge className={getCategoryColor(classInfo?.category)}>
                                {categoryInfo?.label || classInfo?.category || 'Class'}
                              </Badge>
                              <span className="font-mono text-sm text-[#FF007F] font-semibold">
                                {formatTime(schedule.start_time)}
                              </span>
                            </div>
                            
                            <h4 className="font-heading font-semibold text-lg mb-2 text-white">
                              {schedule.class_name || classInfo?.name}
                            </h4>
                            
                            <div className="space-y-1 text-sm text-white/50">
                              <p className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                              </p>
                              <p className="flex items-center gap-2">
                                <MapPin className="w-4 h-4" />
                                {schedule.room_name}
                              </p>
                              {schedule.teacher_name && (
                                <p className="flex items-center gap-2">
                                  <User className="w-4 h-4" />
                                  {schedule.teacher_name}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Schedule;
